import { DictType } from '@pma/types'
import { useAuth } from '@stores/auth-store'
import { toast } from '@hooks/use-toast'
import { DIGITAL_OCEAN_FILES_ENDPOINT, EDIT_TASK_ENDPOINT, SERVER_URL } from '@pma/utils/api-routes'
import { ACCESS_TOKEN, BLOCKED_EXTENSIONS, queryClient } from '@pma/utils/constants'
import { cn } from '@pma/utils/helpers'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { parseCookies } from 'nookies'
import { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Edit2, FileText, Loader, Upload } from 'lucide-react'
import useTranslator from '@hooks/use-translator'
import { Switch } from '@components/common/switch'
import { GroupedTasksContext } from '../grouped-tasks/context/GroupedTasksProvider';

interface Props {
  id: number
  title: string
  children: ReactNode
  value: string | null | undefined
  customId?: boolean
  columnType?: string
  parentTaskId?: number
  selectorList?: string[]
  customFile?: { name?: string; id?: number; file?: string; type?: string }
}

// Convert datetime-local string to Unix timestamp (seconds)
const toUnixTimestamp = (datetimeStr: string): number =>
  Math.floor(new Date(datetimeStr).getTime() / 1000)

// Convert Unix timestamp (seconds) to datetime-local string (local time)
const fromUnixTimestamp = (timestamp: number | string): string => {
  const ts = Number(timestamp)
  if (isNaN(ts) || !ts) return ''
  const date = new Date(ts * 1000)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const CardEditor = ({
  children,
  id,
  title,
  value,
  customId,
  columnType,
  parentTaskId,
  selectorList,
  customFile
}: Props) => {
  const [editMode, setEditMode] = useState(false)
  const [updatedValue, setUpdatedValue] = useState<any>('')
  const [hover, setHover] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext);
  const navigate = useNavigate()

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const userId = authConfig?.id

  useEffect(() => {
    if (value) {
      if (columnType === 'timestamp') {
        setUpdatedValue(fromUnixTimestamp(value))
      } else {
        setUpdatedValue(value)
      }
    }
    return () => {
      setUpdatedValue('')
    }
  }, [value, columnType])

  const updateTaskDetails = async (updatedValue: any) => {
    let payload = {
      task_id: id,
      user_id: userId,
    }

    let updates

    if (customId) {
      updates = {
        custom_columns: [
          {
            column_id: customId,
            value: updatedValue,
          },
        ],
      }
    } else {
      updates = {
        [title]: updatedValue,
      }
    }

    const response = await axios.post(
      SERVER_URL + EDIT_TASK_ENDPOINT,
      { ...payload, ...updates },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      },
    )

    return response.data
  }

  const updateTaskDetailsMutation = useMutation({
    mutationKey: [`${title}`, id],
    mutationFn: updateTaskDetails,
    onSuccess: (data) => {
      // Revalidate tasks details
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({queryKey:['grouped-tasks']})
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any);
      }
    },
    onError: (error: DictType) => {
      const errorMessage =
        error?.response?.data?.message || `Error occurred while updating`
      toast({
        title: `${translate('Error')} - ${title}`,
        description: translate(errorMessage),
        variant: 'error',
      })
    },
  })

  const onSubmit = () => {
    setEditMode(false)
    if (!updatedValue) return
    let submitValue = updatedValue
    if (columnType === 'timestamp') {
      submitValue = toUnixTimestamp(updatedValue)
    }
    if (String(submitValue) !== String(value)) {
      updateTaskDetailsMutation.mutate(submitValue)
    }
  }

  if (columnType === 'boolean' || columnType === 'checkbox') {
    return (
      <>
        {updateTaskDetailsMutation.isPending ? (
          <Loader className='animate-spin text-primary' />
        ) : (
          <Switch
            checked={value === 'true'}
            onCheckedChange={(checked) => {
              updateTaskDetailsMutation.mutate(String(checked))
            }}
            disabled={updateTaskDetailsMutation.isPending}
          />
        )}
      </>
    )
  }

  if (columnType === 'selector') {
    return (
      <div
        className={cn(
          'min-w-[70px] w-[120px] gap-2 text-center text-black font-semibold cursor-pointer hover:border-primary hover:border-3',
          updateTaskDetailsMutation.isPending &&
            'font-light text-transparent rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 animate-shimmer delay-100 duration-150',
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {!editMode && (
          <div className='relative '>
            {hover && (
              <Edit2
                className='absolute -right-2 -top-2 text-primary hover:border-3'
                size={12}
                onClick={(e) => {
                  e.stopPropagation()
                  setEditMode(true)
                }}
              />
            )}
            {children}
          </div>
        )}
        {editMode && (
          <select
            className='rounded border bg-white px-2 py-1 font-normal min-w-[50px] w-[90px] outline-none focus:border-primary'
            value={updatedValue}
            onChange={(e) => {
              setUpdatedValue(e.target.value)
              setEditMode(false)
              if (e.target.value !== value) {
                updateTaskDetailsMutation.mutate(e.target.value)
              }
            }}
            autoFocus
            onBlur={() => setEditMode(false)}
          >
            <option value=''>{translate('Select')}</option>
            {selectorList?.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        )}
      </div>
    )
  }

  if (columnType === 'file') {
    const isImage = customFile?.file?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (!selectedFile) return

      const nameArr = selectedFile.name.split('.')
      const fileExt = nameArr[nameArr.length - 1]
      if (BLOCKED_EXTENSIONS.includes(fileExt)) {
        toast({ title: translate('Error'), description: translate('Please select a valid file!'), variant: 'error' })
        return
      }

      const formData = new FormData()
      formData.append('files', selectedFile)

      try {
        const response = await axios.post(
          `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`,
          formData,
          { headers: { accept: '*/*', 'Content-Type': 'multipart/form-data' } },
        )
        const uploadedFile = response.data
        if (uploadedFile?.id) {
          updateTaskDetailsMutation.mutate(String(uploadedFile.id))
        }
      } catch (error: any) {
        toast({
          title: translate('Error'),
          description: error?.response?.data?.message || translate('Failed to upload file'),
          variant: 'error',
        })
      }

      if (fileInputRef.current) fileInputRef.current.value = ''
    }

    return (
      <div className='flex items-center gap-2'>
        <input ref={fileInputRef} type='file' className='hidden' onChange={handleFileUpload} />
        {updateTaskDetailsMutation.isPending ? (
          <Loader className='animate-spin text-primary' />
        ) : value && customFile?.file ? (
          <div
            className='flex items-center gap-2 cursor-pointer'
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {isImage ? (
              <img
                src={customFile.file}
                alt={customFile.name || 'file'}
                className='h-8 w-8 rounded object-cover border'
              />
            ) : (
              <FileText size={14} className='text-primary' />
            )}
            <span className='text-sm font-normal truncate max-w-[90px]'>
              {customFile.name || `File #${value}`}
            </span>
            {hover && (
              <Edit2
                className='text-primary hover:border-3'
                size={12}
                onClick={() => fileInputRef.current?.click()}
              />
            )}
          </div>
        ) : value ? (
          <div
            className='flex items-center gap-2 cursor-pointer'
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <FileText size={14} className='text-primary' />
            <span className='text-sm font-normal'>File #{value}</span>
            {hover && (
              <Edit2
                className='text-primary hover:border-3'
                size={12}
                onClick={() => fileInputRef.current?.click()}
              />
            )}
          </div>
        ) : (
          <button
            className='flex items-center gap-1 text-sm text-primary hover:underline'
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={14} />
            {translate('Upload')}
          </button>
        )}
      </div>
    )
  }

  if (columnType === 'range') {
    const rangeVal = (() => {
      try {
        const parsed = JSON.parse(value || '{}')
        return {
          from: fromUnixTimestamp(parsed.from),
          to: fromUnixTimestamp(parsed.to),
        }
      } catch {
        return { from: '', to: '' }
      }
    })()

    return (
      <div
        className={cn(
          'min-w-[70px] w-[120px] gap-2 text-center text-black font-semibold cursor-pointer hover:border-primary hover:border-3',
          updateTaskDetailsMutation.isPending &&
            'font-light text-transparent rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 animate-shimmer delay-100 duration-150',
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {!editMode && (
          <div className='relative '>
            {hover && (
              <Edit2
                className='absolute -right-2 -top-2 text-primary hover:border-3'
                size={12}
                onClick={(e) => {
                  e.stopPropagation()
                  setUpdatedValue(JSON.stringify({
                    from: rangeVal.from ? toUnixTimestamp(rangeVal.from) : '',
                    to: rangeVal.to ? toUnixTimestamp(rangeVal.to) : '',
                  }))
                  setEditMode(true)
                }}
              />
            )}
            {children}
          </div>
        )}
        {editMode && (
          <div className='flex flex-col gap-1'>
            <input
              type='datetime-local'
              className='rounded border bg-white/20 px-1 text-sm font-normal min-w-[50px] w-[90px] outline-none focus:border-primary'
              defaultValue={rangeVal.from}
              autoFocus
              onChange={(e) => {
                const current = (() => {
                  try { return JSON.parse(updatedValue || '{}') }
                  catch { return { from: '', to: '' } }
                })()
                setUpdatedValue(JSON.stringify({
                  ...current,
                  from: e.target.value ? toUnixTimestamp(e.target.value) : '',
                }))
              }}
            />
            <input
              type='datetime-local'
              className='rounded border bg-white/20 px-1 text-sm font-normal min-w-[50px] w-[90px] outline-none focus:border-primary'
              defaultValue={rangeVal.to}
              onBlur={() => onSubmit()}
              onChange={(e) => {
                const current = (() => {
                  try { return JSON.parse(updatedValue || '{}') }
                  catch { return { from: '', to: '' } }
                })()
                setUpdatedValue(JSON.stringify({
                  ...current,
                  to: e.target.value ? toUnixTimestamp(e.target.value) : '',
                }))
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                onSubmit()
              }}
            />
          </div>
        )}
      </div>
    )
  }

  const inputType =
    columnType === 'date'
      ? 'date'
      : columnType === 'timestamp'
        ? 'datetime-local'
        : 'text'

  return (
    <div
      className={cn(
        'min-w-[70px] w-[120px] gap-2 text-center text-black font-semibold cursor-pointer hover:border-primary hover:border-3',
        updateTaskDetailsMutation.isPending &&
          'font-light text-transparent rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 animate-shimmer delay-100 duration-150',
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        if(editMode){
          onSubmit()
        }else{
          setEditMode(true)}}
        }
    >
      {!editMode && (
        <div className='relative '>
          {hover && (
            <Edit2
              className='absolute -right-2 -top-2 text-primary hover:border-3'
              size={12}
            />
          )}
          {children}
        </div>
      )}
      {editMode && (
        <input
          type={inputType}
          className='rounded border bg-white/20 px-2 font-normal min-w-[50px] w-[90px] outline-none focus:border-primary'
          value={updatedValue}
          onChange={(e) => setUpdatedValue(e?.target?.value)}
          autoFocus
          onBlur={() => {
            onSubmit()
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            onSubmit()
          }}
        />
      )}
    </div>
  )
}

export default CardEditor
